/**
 * Time formatting utilities.
 *
 * Root cause context: server/db.js returns all Postgres timestamps as raw
 * strings (types.setTypeParser disabled). TIMESTAMP WITHOUT TIME ZONE columns
 * arrive as "2026-08-01 09:27:10" (no Z), TIMESTAMPTZ as "2026-08-01 09:27:10+00"
 * (non-standard offset). Both must be normalized to ISO UTC before parsing.
 */

/**
 * The business runs in Austin, so "today" is an Austin calendar date — not the
 * browser's, and never UTC. `new Date().toISOString().slice(0,10)` rolls over
 * at 7pm local (6pm in winter), which put tomorrow's date on tonight's new
 * jobs, tonight's dashboard and tonight's availability blocks. The server made
 * exactly this fix in `server/utils/businessDate.js`; this is its twin.
 */
export const BUSINESS_TZ = 'America/Chicago';

/** Today's calendar date in Austin as YYYY-MM-DD, whatever the device is set to. */
export function businessToday(now: Date = new Date()): string {
  // en-CA renders as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

/** A YYYY-MM-DD date shifted by N days, staying on calendar dates (DST-safe). */
export function isoPlusDays(dateISO: string, days: number): string {
  const d = new Date(dateISO.slice(0, 10) + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Normalize a Postgres timestamp string and parse it as a UTC Date. */
export function parseDbTs(ts: string | null | undefined): Date | null {
  if (!ts) return null;
  let s = ts.replace(' ', 'T');         // space separator → T
  s = s.replace(/([+-]\d{2})$/, '$1:00'); // "+00" → "+00:00"
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z'; // no TZ → treat as UTC
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a Postgres timestamp in business time (America/Chicago) — never the device's zone. */
export function fmtTs(
  ts: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  const d = parseDbTs(ts);
  if (!d) return '—';
  return d.toLocaleString('en-US', { timeZone: BUSINESS_TZ, ...opts });
}

/**
 * A job's planned window, the same on every screen. time_start/time_end are
 * Chicago "HH:MM" strings; an end that is missing or before the start is
 * replaced by start + estimate, so "12:53 PM–10:18 AM" can never render.
 * kind: 'firm' (a promise) or 'eta' (moves as the day runs).
 */
export function jobWindow(job: any): { start: string | null; end: string | null; kind: 'firm' | 'eta' | null } {
  const toMins = (t: any) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(t || '')); if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return null; return Number(m[1]) * 60 + Number(m[2]); };
  const s = toMins(job?.time_start);
  if (s == null) return { start: null, end: null, kind: null };
  const est = Number(job?.estimated_minutes_snapshot) > 0 ? Number(job.estimated_minutes_snapshot) : 45;
  const e = toMins(job?.time_end);
  const endMins = e != null && e >= s ? e : s + est;
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    start: String(job.time_start).slice(0, 5),
    end: `${pad(Math.floor((endMins % 1440) / 60))}:${pad(endMins % 60)}`,
    kind: job?.time_is_firm ? 'firm' : 'eta',
  };
}

/** "ETA 3:13 PM–3:33 PM" / "Scheduled 2:00 PM–2:45 PM" / '' when the stop has no time. */
export function fmtWindow(job: any, { label = true }: { label?: boolean } = {}): string {
  // What happened beats what was planned: a finished stop shows its measured
  // start–finish, a running one its start, both in business time.
  if (job?.status === 'complete' && job?.actual_start_time) {
    const span = `${fmtTsTime(job.actual_start_time)}${job.actual_end_time ? `–${fmtTsTime(job.actual_end_time)}` : ''}`;
    return label ? `Done ${span}` : span;
  }
  if (job?.status === 'in_progress' && job?.actual_start_time) {
    return label ? `Started ${fmtTsTime(job.actual_start_time)}` : fmtTsTime(job.actual_start_time);
  }
  const w = jobWindow(job);
  if (!w.start) return '';
  const span = `${fmtClock(w.start)}${w.end ? `–${fmtClock(w.end)}` : ''}`;
  return label ? `${w.kind === 'firm' ? 'Scheduled' : 'ETA'} ${span}` : span;
}

/** Format only the time portion (e.g. "4:27 AM"). */
export function fmtTsTime(ts: string | null | undefined): string {
  return fmtTs(ts, { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Format a YYYY-MM-DD date string without UTC midnight date-shifting. */
export function fmtLocalDate(
  dateStr: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', opts);
}

/** Clock time for people: "14:05" | "14:05:00" → "2:05 PM"; empty/invalid → ''. */
export function fmtClock(t: string | null | undefined): string {
  if (!t) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t));
  if (!m) return String(t);
  const h = Number(m[1]);
  if (h > 23) return String(t);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}
